import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireClient, RequireMaster } from '@/features/auth/components/RequireSession';
import { COMPETITOR_IDS, COMPETITORS } from '@/features/comparison';
import type { InfoPageId } from '@/features/marketing/model/infoPages';
import { SettingsLayout } from '@/features/settings/components/SettingsLayout';
import { BareLayout } from '@/layouts/BareLayout';
import { CatalogLayout } from '@/layouts/CatalogLayout';
import { ClientHubLayout } from '@/layouts/ClientHubLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { MarketingLayout } from '@/layouts/MarketingLayout';
import { PageSeo, type SeoPage } from '@/seo';
import { servedShowcase } from '@/shared/config/customDomain';
import { ROUTES } from '@/shared/config/routes';

import * as Page from './lazyPages';

/**
 * The informational and legal pages share one component and differ by id;
 * listing them as data keeps the route table from repeating the element.
 */
const INFO_PAGES: { path: string; page: InfoPageId; seo: SeoPage }[] = [
  { path: ROUTES.about, page: 'about', seo: 'about' },
  { path: ROUTES.blog, page: 'blog', seo: 'blog' },
  { path: ROUTES.contact, page: 'contact', seo: 'contact' },
  { path: ROUTES.privacy, page: 'privacy', seo: 'privacy' },
  { path: ROUTES.terms, page: 'terms', seo: 'terms' },
  { path: ROUTES.support, page: 'support', seo: 'support' },
  { path: ROUTES.rules, page: 'rules', seo: 'rules' },
  { path: ROUTES.reviews, page: 'reviews', seo: 'reviews' },
  { path: ROUTES.updates, page: 'updates', seo: 'updates' },
];

/** A page with its registry head tags; the pair is one route element. */
const withSeo = (page: SeoPage, element: ReactNode) => (
  <>
    <PageSeo page={page} />
    {element}
  </>
);

/**
 * The route table.
 *
 * Routes are grouped by layout, so whether a page shows the public header and
 * footer is a structural fact rather than a pathname check inside the chrome.
 */
export const AppRoutes = () => (
  <Routes>
    <Route element={<MarketingLayout />}>
      {!servedShowcase() && (
        <Route path={ROUTES.home} element={withSeo('home', <Page.LandingPage />)} />
      )}
      <Route path={ROUTES.features} element={withSeo('features', <Page.FeaturesPage />)} />
      <Route path={ROUTES.pricing} element={withSeo('pricing', <Page.PricingPage />)} />
      {INFO_PAGES.map(({ path, page, seo }) => (
        <Route key={path} path={path} element={withSeo(seo, <Page.InfoPage page={page} />)} />
      ))}
      {COMPETITOR_IDS.map((id) => (
        <Route
          key={id}
          path={COMPETITORS[id].path}
          element={<Page.ComparisonPage competitor={id} />}
        />
      ))}
      <Route path="*" element={withSeo('notFound', <Page.NotFoundPage />)} />
    </Route>

    <Route element={<CatalogLayout />}>
      <Route path={ROUTES.explore} element={withSeo('explore', <Page.ExplorePage />)} />
      <Route path={ROUTES.masterPattern} element={<Page.MasterProfilePage />} />
      {/* A master's own domain: their showcase is the home page. */}
      {servedShowcase() && <Route path={ROUTES.home} element={<Page.MasterProfilePage />} />}
    </Route>

    <Route element={<BareLayout />}>
      <Route path={ROUTES.auth} element={<Page.AuthPage />} />
      <Route path={ROUTES.resetPassword} element={<Page.ResetPasswordPage />} />
      <Route path={ROUTES.clientLogin} element={<Page.ClientAuthPage />} />
      {/* The wizard fills the screen and deliberately has no dashboard chrome. */}
      <Route path={ROUTES.app.onboarding} element={<Page.OnboardingWizardPage />} />
    </Route>

    <Route element={<RequireClient />}>
      <Route element={<ClientHubLayout />}>
        <Route path={ROUTES.client.root} element={<Page.ClientDashboardPage />} />
        <Route path={ROUTES.client.bookings} element={<Page.ClientBookingsPage />} />
        <Route path={ROUTES.client.favorites} element={<Page.ClientFavoritesPage />} />
        <Route path={ROUTES.client.profile} element={<Page.ClientProfilePage />} />
        <Route
          path={ROUTES.client.settings}
          element={<Navigate to={ROUTES.client.profile} replace />}
        />
      </Route>
    </Route>

    <Route element={<RequireMaster />}>
      <Route element={<DashboardLayout />}>
        <Route path={ROUTES.app.root} element={<Page.DashboardPage />} />
        <Route path={ROUTES.app.calendar} element={<Page.CalendarPage />} />
        <Route path={ROUTES.app.notifications} element={<Page.NotificationsPage />} />
        <Route path={ROUTES.app.team} element={<Page.TeamPage />} />
        {/* Accepting an invitation: the invitee is signed in as themselves and
            belongs to no studio yet, so this sits beside the team page rather
            than inside it. */}
        <Route path={ROUTES.app.teamJoin} element={<Page.TeamJoinPage />} />
        <Route path={ROUTES.app.clients} element={<Page.ClientsPage />} />
        <Route path={ROUTES.app.reviews} element={<Page.ReviewsPage />} />
        <Route path={ROUTES.app.analytics} element={<Page.AnalyticsPage />} />
        <Route path={ROUTES.app.services} element={<Page.ServicesPage />} />
        <Route path={ROUTES.app.portfolio} element={<Page.PortfolioPage />} />
        <Route element={<SettingsLayout />}>
          <Route path={ROUTES.app.billing} element={<Page.SettingsSubscriptionPage />} />
          <Route path={ROUTES.app.settingsProfile} element={<Page.SettingsProfilePage />} />
          <Route path={ROUTES.app.settingsSchedule} element={<Page.SettingsSchedulePage />} />
          <Route
            path={ROUTES.app.settingsNotifications}
            element={<Page.SettingsNotificationsPage />}
          />
          <Route
            path={ROUTES.app.settingsSubscription}
            element={<Page.SettingsSubscriptionPage />}
          />
          <Route path={ROUTES.app.settingsPrivacy} element={<Page.SettingsPrivacyPage />} />
        </Route>
        <Route
          path={ROUTES.app.settings}
          element={<Navigate to={ROUTES.app.settingsProfile} replace />}
        />
      </Route>
    </Route>
  </Routes>
);
